import { useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getServerUrl } from '../../lib/getServerUrl';
import { Users, BookOpen, MessageSquare, Monitor, Eye } from 'lucide-react';
import SheetVoteResults from '../../display/pages/SheetVoteResults';
import ConsiglioMasterView from './ConsiglioMasterView';
import HeroSheetsTab from './HeroSheetsTab';
import SoundboardPanel from '../components/SoundboardPanel';
import AudiencePreview from '../components/AudiencePreview';

interface ConsiglioEntry {
  id: string;
  text: string;
  nickname: string;
  team: string;
  timestamp: number;
}

type MasterTab = 'eroi-attivi' | 'schede-eroi' | 'consigli' | 'display-preview' | 'audience-preview';

function MasterMain() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<MasterTab>('eroi-attivi');
  const [consiglioActive, setConsiglioActive] = useState(false);
  const [consiglioEntries, setConsiglioEntries] = useState<ConsiglioEntry[]>([]);
  const [viewSheetId, setViewSheetId] = useState<string | null>(null);
  const [viewTeamColor, setViewTeamColor] = useState<string | null>(null);

  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await fetch('/api/sessions/active');
      if (response.ok) {
        const data = await response.json();
        if (data.session) {
          setSessionId(data.session.id);
        }
      }
    } catch (error) {
      console.error('[Master] Error fetching active session:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-detect active session
  useEffect(() => {
    fetchActiveSession();
  }, [fetchActiveSession]);

  // Connect to Socket.IO display namespace for real-time updates
  useEffect(() => {
    if (!sessionId) return;

    const serverUrl = getServerUrl('display');
    const newSocket = io(serverUrl, {
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('[Master] Connected to Socket.IO');
      newSocket.emit('display:join-session', { sessionId });
    });

    newSocket.on('display:projection-update', (data: { mode: any }) => {
      // Re-fetch winners when projection mode changes (new winner selected)
      setRefreshKey((k) => k + 1);

      // Detect consiglio mode - auto-switch to consigli tab
      if (data.mode?.type === 'consiglio') {
        setConsiglioActive(true);
        setConsiglioEntries([]);
        setActiveTab('consigli');
      } else {
        setConsiglioActive(false);
      }
    });

    newSocket.on('consiglio:new-entry', (data: { entry: ConsiglioEntry }) => {
      setConsiglioEntries((prev) => [...prev, data.entry]);
    });

    newSocket.on('consiglio:closed', () => {
      setConsiglioActive(false);
      // Entries NON vengono cancellate - restano visibili fino alla prossima apertura
    });

    newSocket.on('session:state-change', () => {
      // Re-fetch on session state changes too
      setRefreshKey((k) => k + 1);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-purple-500 mx-auto mb-8"></div>
          <p className="text-3xl text-gray-300">Ricerca sessione attiva...</p>
        </div>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Nessuna sessione attiva</h1>
          <p className="text-xl text-gray-400">In attesa di una sessione...</p>
          <button
            onClick={() => { setIsLoading(true); fetchActiveSession(); }}
            className="mt-6 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  const handleHeroClick = (sheetId: string, teamColor: string) => {
    setViewSheetId(sheetId);
    setViewTeamColor(teamColor);
    setActiveTab('schede-eroi');
  };

  const tabs: { id: MasterTab; label: string; icon: typeof Users }[] = [
    { id: 'eroi-attivi', label: 'Eroi attivi', icon: Users },
    { id: 'schede-eroi', label: 'Schede eroi', icon: BookOpen },
    { id: 'consigli', label: 'Consigli dal pubblico', icon: MessageSquare },
    { id: 'display-preview', label: 'Display', icon: Monitor },
    { id: 'audience-preview', label: 'Audience', icon: Eye },
  ];

  return (
    <div className="h-screen flex overflow-hidden">
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Tab bar */}
        <div className="flex-shrink-0 bg-gray-900 border-b border-gray-700 flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const hasNotification = tab.id === 'consigli' && consiglioActive && activeTab !== 'consigli';
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition relative ${
                  isActive
                    ? 'text-purple-300 border-b-2 border-purple-500 bg-gray-800/50'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/30'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {hasNotification && (
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'eroi-attivi' && (
            <SheetVoteResults key={refreshKey} sessionId={sessionId} showAllTeams={true} onHeroClick={handleHeroClick} />
          )}
          {activeTab === 'schede-eroi' && (
            <HeroSheetsTab selectedSheetId={viewSheetId} teamColor={viewTeamColor} onSheetViewed={() => { setViewSheetId(null); setViewTeamColor(null); }} />
          )}
          {activeTab === 'consigli' && (
            <ConsiglioMasterView entries={consiglioEntries} sessionId={sessionId} />
          )}
          {activeTab === 'display-preview' && (
            <div className="h-full bg-black flex items-center justify-center p-4">
              <iframe
                src={`/display?session=${sessionId}`}
                className="w-full h-full rounded-lg border border-gray-700"
                style={{ aspectRatio: '16/9', maxHeight: '100%' }}
                title="Display Preview"
              />
            </div>
          )}
          {activeTab === 'audience-preview' && (
            <AudiencePreview sessionId={sessionId} />
          )}
        </div>
      </div>
      <div className="w-[280px] flex-shrink-0 border-l border-gray-700">
        <SoundboardPanel autoPlayEnabled={false} />
      </div>
    </div>
  );
}

export default MasterMain;
